/**
 * Watrly Theme & Language Context
 * ================================
 * سياق الثيم واللغة
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { translations, defaultLocale, localeConfig, t as translate } from '../i18n';

const ThemeContext = createContext(null);

export function ThemeProvider({ children, initialLocale = defaultLocale }) {
  const [locale, setLocale] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('watrly-locale') || initialLocale;
    }
    return initialLocale;
  });

  const [dir, setDir] = useState(localeConfig[locale]?.dir || 'rtl');

  // تحديث اتجاه الصفحة عند تغيير اللغة
  useEffect(() => {
    const config = localeConfig[locale];
    if (config) {
      setDir(config.dir);
      document.documentElement.setAttribute('dir', config.dir);
      document.documentElement.setAttribute('lang', locale);
      localStorage.setItem('watrly-locale', locale);
    }
  }, [locale]);

  // دالة الترجمة
  const t = useCallback((path, params = {}) => {
    return translate(path, locale, params);
  }, [locale]);

  // تغيير اللغة
  const changeLocale = useCallback((newLocale) => {
    if (localeConfig[newLocale]) {
      setLocale(newLocale);
    }
  }, []);

  // تبديل اللغة
  const toggleLocale = useCallback(() => {
    setLocale(prev => prev === 'ar' ? 'en' : 'ar');
  }, []);

  const value = {
    locale,
    dir,
    isRTL: dir === 'rtl',
    t,
    changeLocale,
    toggleLocale,
    localeConfig: localeConfig[locale],
    translations: translations[locale],
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

export function useTranslation() {
  const { t, locale, dir, isRTL } = useTheme();
  return { t, locale, dir, isRTL };
}

export default ThemeContext;
